import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { apiGetSiteWorksPartners } from '@/features/jobs/api/api-get-site-works-partners'
import { apiGetUsers } from '@/features/jobs/api/api-get-users'
import type { ReportCategoryChartType } from '@/features/reports/components/report-category-chart'
import type { JobsReportFilterLabels } from '@/features/reports/lib/format-jobs-report-filter-title'
import type { JobsReportSort } from '@/features/reports/lib/jobs-report-sort'
import type { JobsReportBaseFilters } from '@/features/reports/types/jobs-report'
import { emptyPagination } from '@/lib/api/empty-pagination'
import type { FilterOption, Pagination } from '@/lib/api/types'

type PartnerLabeled = {
  assigned_partner_id: number
  assigned_partner_name: string
}

type JobsReportSearchState<TFilters> = {
  filters: TFilters
  page: number
  chartType: ReportCategoryChartType
  sort: JobsReportSort
}

type JobsReportFetchResult<TJob, TSummary> = {
  data: TJob[]
  pagination: Pagination
  summary: TSummary
}

type JobsReportState<TFilters, TJob, TSummary> = {
  fetchKey: string
  fetchReport: JobsReportFetcher<TFilters, TJob, TSummary>
  report: JobsReportFetchResult<TJob, TSummary> | null
  error: string | null
}

type JobsReportFetcher<TFilters, TJob, TSummary> = (
  filters: TFilters,
  page: number,
  sort?: JobsReportSort
) => Promise<JobsReportFetchResult<TJob, TSummary>>

type UseJobsReportPageArgs<TFilters extends JobsReportBaseFilters, TJob, TSummary> =
  {
    parseSearchParams: (
      searchParams: URLSearchParams
    ) => JobsReportSearchState<TFilters>
    buildSearchParams: (
      filters: TFilters,
      page: number,
      chartType?: ReportCategoryChartType,
      sort?: JobsReportSort
    ) => URLSearchParams
    defaultSearchParams: () => URLSearchParams
    fetchReport: JobsReportFetcher<TFilters, TJob, TSummary>
    emptySummary: () => TSummary
    formatFilterTitle: (
      filters: TFilters,
      labels: JobsReportFilterLabels
    ) => string
    loadErrorMessage: string
    extraPartnerRows?: (summary: TSummary) => PartnerLabeled[]
  }

const NO_JOBS: never[] = []

const toLabelMap = (options: FilterOption[]) =>
  new Map(options.map((option) => [option.id, option.label]))

export const useJobsReportPage = <
  TFilters extends JobsReportBaseFilters,
  TJob extends PartnerLabeled,
  TSummary,
>({
  parseSearchParams,
  buildSearchParams,
  defaultSearchParams,
  fetchReport,
  emptySummary,
  formatFilterTitle,
  loadErrorMessage,
  extraPartnerRows,
}: UseJobsReportPageArgs<TFilters, TJob, TSummary>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [reportState, setReportState] =
    useState<JobsReportState<TFilters, TJob, TSummary> | null>(null)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [raisedByUsers, setRaisedByUsers] = useState<FilterOption[]>([])
  const [completedByUsers, setCompletedByUsers] = useState<FilterOption[]>([])
  const [partners, setPartners] = useState<FilterOption[]>([])

  const { filters, page, chartType, sort } = useMemo(
    () => parseSearchParams(searchParams),
    [parseSearchParams, searchParams]
  )

  const reportFetchKey = useMemo(
    () => buildSearchParams(filters, page, 'pie', sort).toString(),
    [buildSearchParams, filters, page, sort]
  )

  const updateSearch = useCallback(
    (
      nextFilters: TFilters,
      nextPage: number,
      nextChartType: ReportCategoryChartType = chartType,
      nextSort: JobsReportSort = sort
    ) => {
      setSearchParams(
        buildSearchParams(nextFilters, nextPage, nextChartType, nextSort)
      )
    },
    [buildSearchParams, chartType, setSearchParams, sort]
  )

  useEffect(() => {
    if ([...searchParams.keys()].length > 0) {
      return
    }

    setSearchParams(defaultSearchParams(), { replace: true })
  }, [defaultSearchParams, searchParams, setSearchParams])

  const hasSearchParams = searchParams.toString() !== ''
  const reportRequest = useMemo(
    () => parseSearchParams(new URLSearchParams(reportFetchKey)),
    [parseSearchParams, reportFetchKey]
  )

  useEffect(() => {
    if (!hasSearchParams) {
      return
    }

    let isCurrent = true

    fetchReport(reportRequest.filters, reportRequest.page, reportRequest.sort).then(
      (report) => {
        if (isCurrent) {
          setReportState({
            fetchKey: reportFetchKey,
            fetchReport,
            report,
            error: null,
          })
        }
      },
      (loadError: unknown) => {
        if (isCurrent) {
          setReportState((current) => ({
            fetchKey: reportFetchKey,
            fetchReport,
            report: current?.report ?? null,
            error: loadError instanceof Error ? loadError.message : loadErrorMessage,
          }))
        }
      }
    )

    return () => {
      isCurrent = false
    }
  }, [fetchReport, hasSearchParams, loadErrorMessage, reportFetchKey, reportRequest])

  const report = reportState?.report ?? null
  const isLoading =
    reportState?.fetchKey !== reportFetchKey ||
    reportState.fetchReport !== fetchReport
  const error = isLoading ? null : (reportState?.error ?? null)
  const jobs: TJob[] = report?.data ?? NO_JOBS
  const pagination = report?.pagination ?? emptyPagination()
  const fallbackSummary = useMemo(() => emptySummary(), [emptySummary])
  const summary = report?.summary ?? fallbackSummary

  useEffect(() => {
    const loadFilterLabels = async () => {
      try {
        const [raisedBy, completedBy, partnerOptions] = await Promise.all([
          apiGetUsers({ exclude_role: 'site_worker' }),
          apiGetUsers({ role: 'site_worker' }),
          apiGetSiteWorksPartners(),
        ])

        setRaisedByUsers(raisedBy)
        setCompletedByUsers(completedBy)
        setPartners(partnerOptions)
      } catch {
        // Title falls back to id labels when option lookups fail.
      }
    }

    void loadFilterLabels()
  }, [])

  const partnerLabels = useMemo(() => {
    const labels = toLabelMap(partners)

    for (const job of jobs) {
      if (!labels.has(job.assigned_partner_id)) {
        labels.set(job.assigned_partner_id, job.assigned_partner_name)
      }
    }

    for (const row of extraPartnerRows?.(summary) ?? []) {
      if (!labels.has(row.assigned_partner_id)) {
        labels.set(row.assigned_partner_id, row.assigned_partner_name)
      }
    }

    return labels
  }, [extraPartnerRows, jobs, partners, summary])

  const chartTitle = useMemo(
    () =>
      formatFilterTitle(filters, {
        raisedByUsers: toLabelMap(raisedByUsers),
        completedByUsers: toLabelMap(completedByUsers),
        partners: partnerLabels,
      }),
    [
      completedByUsers,
      filters,
      formatFilterTitle,
      partnerLabels,
      raisedByUsers,
    ]
  )

  return {
    filters,
    page,
    chartType,
    sort,
    jobs,
    summary,
    pagination,
    isInitialLoading: isLoading && report === null,
    isFetching: isLoading,
    error,
    isFiltersOpen,
    setIsFiltersOpen,
    updateSearch,
    chartTitle,
  }
}
